'server-only'

import { createAdminClient } from "@/lib/supabase/admin"
import { 
    EmailRecord, 
    EmailTemplate, 
    WebhookEvent, 
    Unsubscribe,
    EmailList 
} from "@/lib/types/resend-types"
import { logErrorAsAdmin } from "@/lib/supabase/queries"
import { v4 as uuidv4 } from "uuid"

// Email CRUD operations
export async function createEmailRecord(email: Omit<EmailRecord, 'id' | 'created_at' | 'sent_at'>): Promise<EmailRecord | null> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("emails")
            .insert({
                user_id: email.user_id,
                recipient: email.recipient,
                subject: email.subject,
                content: email.content,
                template_name: email.template_name,
                metadata: email.metadata || {},
                status: email.status,
            })
            .select()
            .single()

        if (error) {
            console.error(`[createEmailRecord] Database error:`, error)
            throw error
        }
        return data as EmailRecord
    } catch (error) {
        console.error(`[createEmailRecord] Full error details:`, {
            error,
            message: (error as Error).message,
            stack: (error as Error).stack,
            emailData: email
        })
        
        await logErrorAsAdmin({
            userId: email.user_id,
            message: `Failed to create email record: ${(error as Error).message}`,
            type: "EMAIL_RECORD_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error: (error as Error).message, email }
        })
        return null
    }
}

export async function updateEmailStatus(id: string, status: EmailRecord['status'], sent_at?: string): Promise<boolean> {
    console.log('udpating email status', status)
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("emails")
            .update({ status, sent_at })
            .eq("id", id)

        console.log('updated email status', data, error)

        if (error) throw error
        return true
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to update email status",
            type: "EMAIL_STATUS_UPDATE_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  id, status }
        })
        return false
    }
}

// Email template operations
export async function createEmailTemplate(template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<EmailTemplate | null> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("email_templates")
            .insert({
                name: template.name,
                subject: template.subject,
                content: template.content
            })
            .select()
            .single()

        if (error) throw error
        return data as EmailTemplate
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to create email template",
            type: "TEMPLATE_CREATION_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  template }
        })
        return null
    }
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("email_templates")
            .select("*")
            .order("name")

        if (error) throw error
        return data as EmailTemplate[]
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to fetch email templates",
            type: "TEMPLATE_FETCH_ERROR",
            status: 500,
            stackTrace: (error as Error).stack
        })
        return []
    }
}

export async function getEmailTemplateByName(name: string): Promise<EmailTemplate | null> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("email_templates")
            .select("*")
            .eq("name", name)
            .single()

        if (error) throw error
        return data as EmailTemplate
    } catch (error) {
        await logErrorAsAdmin({
            message: `Failed to fetch email template by name: ${name}`,
            type: "TEMPLATE_FETCH_ERROR",
            status: 500,
            stackTrace: (error as Error).stack
        })
        return null
    }
}

// Email list operations
export async function createEmailList(list: Omit<EmailList, 'id' | 'created_at'>): Promise<EmailList | null> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("email_lists")
            .insert({
                name: list.name,
                description: list.description
            })
            .select()
            .single()

        if (error) throw error
        return data as EmailList
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to create email list",
            type: "EMAIL_LIST_CREATION_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  list }
        })
        return null
    }
}

export async function getEmailLists(): Promise<EmailList[]> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("email_lists")
            .select("*")
            .order("name")

        if (error) throw error
        return data as EmailList[]
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to fetch email lists",
            type: "EMAIL_LIST_FETCH_ERROR",
            status: 500,
            stackTrace: (error as Error).stack
        })
        return []
    }
}

// Webhook events
export async function createWebhookEvent(event: Omit<WebhookEvent, 'id' | 'created_at'>): Promise<WebhookEvent | null> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("resent_events")
            .insert({
                email_id: event.email_id,
                event_type: event.event_type,
                payload: event.payload
            })
            .select()
            .single()

        if (error) throw error
        return data as WebhookEvent
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to create webhook event",
            type: "WEBHOOK_EVENT_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  event }
        })
        return null
    }
}

// Unsubscribe management
export async function addUnsubscribe(email: string, listName: string, userId?: string, reason?: string): Promise<boolean> {
    try {
        const supabase = await createAdminClient()
        const { error } = await supabase
            .from("unsubscribes")
            .insert({
                email,
                user_id: userId,
                list_name: listName,
                reason
            })

        if (error) throw error
        return true
    } catch (error) {
        await logErrorAsAdmin({
            userId,
            message: "Failed to add unsubscribe record",
            type: "UNSUBSCRIBE_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  email, listName, userId, reason }
        })
        return false
    }
}

export async function checkUnsubscribed(email: string, listName: string): Promise<boolean> {
    try {
        const supabase = await createAdminClient()
        const { data, error } = await supabase
            .from("unsubscribes")
            .select("id")
            .eq("email", email)
            .eq("list_name", listName)
            .maybeSingle()

        if (error) throw error
        return !!data
    } catch (error) {
        await logErrorAsAdmin({
            message: "Failed to check unsubscribe status",
            type: "UNSUBSCRIBE_CHECK_ERROR",
            status: 500,
            stackTrace: (error as Error).stack,
            additionalInfo: { error,  email, listName }
        })
        return false // Default to not unsubscribed in case of error
    }
} 